import { OPS_FOR_TYPE, matchesCustomFilter } from "@astrum/shared";
import type { CustomField, CustomFilter, CustomFilterOp, CustomFieldOption } from "@astrum/shared";
import type { Task } from "./api.js";

/*
 * Pure helpers for project custom fields: how a value reads, sorts, filters and
 * exports. Person values are user ids; `people` resolves them to names.
 */

export type PeopleNames = ReadonlyMap<string, string>;

export const FIELD_TYPE_LABELS: Record<CustomField["type"], string> = {
  text: "Text",
  number: "Number",
  dropdown: "Dropdown",
  date: "Date",
  person: "Person",
};

export const FILTER_OP_LABELS: Record<CustomFilterOp, string> = {
  eq: "is",
  contains: "contains",
  gte: "at least",
  lte: "at most",
  in: "is any of",
};

/** The ops a type offers, as the filter UI lists them. A date reads better as on or after / before. */
export function opsFor(type: CustomField["type"]): readonly CustomFilterOp[] {
  return OPS_FOR_TYPE[type];
}

export function optionOf(field: Pick<CustomField, "options">, id: string | number | undefined): CustomFieldOption | undefined {
  return id === undefined ? undefined : field.options.find((o) => o.id === id);
}

/** What a cell or export shows for a value. Empty string when the value is not set. */
export function formatCustomValue(field: CustomField, value: string | number | undefined, people: PeopleNames = new Map()): string {
  if (value === undefined) return "";
  switch (field.type) {
    case "dropdown":
      return optionOf(field, value)?.label ?? "";
    case "person":
      return people.get(String(value)) ?? "";
    default:
      return String(value);
  }
}

/** Sort key for a `cf:` column. Numbers sort numerically, everything else by its shown text. */
export function customSortValue(
  task: Pick<Task, "customValues">,
  fieldId: string,
  fields: readonly CustomField[] = [],
  people: PeopleNames = new Map(),
): string | number | null {
  const value = task.customValues?.[fieldId];
  if (value === undefined) return null;
  const field = fields.find((f) => f.id === fieldId);
  if (!field) return typeof value === "number" ? value : value.toLowerCase();
  if (field.type === "number") return Number(value);
  if (field.type === "dropdown") {
    // Options sort in the order the project defined them, not alphabetically.
    const index = field.options.findIndex((o) => o.id === value);
    return index < 0 ? null : index;
  }
  if (field.type === "person") return (people.get(String(value)) ?? String(value)).toLowerCase();
  return String(value).toLowerCase();
}

/** AND across filters. A filter on a field that no longer exists matches nothing, like the server. */
export function taskMatchesCustomFilters(
  task: Pick<Task, "customValues">,
  filters: readonly CustomFilter[],
  fields: readonly CustomField[],
): boolean {
  return filters.every((filter) => {
    const field = fields.find((f) => f.id === filter.fieldId);
    return !!field && matchesCustomFilter(field, filter, task.customValues?.[filter.fieldId]);
  });
}

/** The text a number input holds back as the API value: null clears, NaN is rejected as undefined. */
export function parseNumberInput(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

/** A filter's value split for the multi-select UIs. */
export const filterValues = (filter: Pick<CustomFilter, "value">): string[] => (filter.value ? filter.value.split(",") : []);

/**
 * Dropdown options from a textarea, one label per line. A label that already
 * exists keeps its id (and colour), so editing the list never wipes the values
 * people chose; a new label gets a fresh id. Blank and repeated lines are dropped.
 */
export function optionsFromText(text: string, existing: readonly CustomFieldOption[], newId: () => string): CustomFieldOption[] {
  const seen = new Set<string>();
  const out: CustomFieldOption[] = [];
  for (const line of text.split("\n")) {
    const label = line.trim().slice(0, 60);
    if (!label || seen.has(label.toLowerCase())) continue;
    seen.add(label.toLowerCase());
    const old = existing.find((o) => o.label.toLowerCase() === label.toLowerCase());
    out.push(old ? { ...old, label } : { id: newId(), label, color: "gray" });
  }
  return out;
}

/** A move of one position in a list, for the reorder buttons. Out of range returns the list unchanged. */
export function moved<T>(list: readonly T[], index: number, by: -1 | 1): T[] {
  const to = index + by;
  if (to < 0 || to >= list.length) return [...list];
  const next = [...list];
  [next[index], next[to]] = [next[to]!, next[index]!];
  return next;
}

/** The repeatable `cf` query values the API takes: `<fieldId>:<op>:<value>`. Filters with no value yet are skipped. */
export function cfQueryValues(filters: readonly CustomFilter[] | undefined): string[] {
  return (filters ?? []).filter((f) => f.value !== "").map((f) => `${f.fieldId}:${f.op}:${f.value}`);
}

/** Extra CSV headers and cells for a project's fields, in field order. */
export function customCsvColumns(
  fields: readonly CustomField[],
  people: PeopleNames,
): { headers: string[]; cells: (task: Pick<Task, "customValues">) => string[] } {
  return {
    headers: fields.map((f) => f.name),
    cells: (task) => fields.map((f) => formatCustomValue(f, task.customValues?.[f.id], people)),
  };
}
